import styles from '../provide-transactions/ProvideTransactions.module.css';
import Text from '../../../../../atoms/typography/Text';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../../../../enums';
import screeningSteps, { Action } from '../../../../../organisms/screening-navigation/screeningSteps';
import { Card } from '../../../../../organisms/card';
import { Trans, useTranslation } from 'react-i18next';
import { Tooltip } from '../../../../../atoms/tooltip';
import QuestionLineIcon from '../../../../../atoms/icons/QuestionLineIcon';
import { RadioInput } from '../../../../../atoms/radio-input';
import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ScreeningNavigation from '../../../../../organisms/screening-navigation/ScreeningNavigation';
import { useRoutePath } from '../../../../../utils/useRoutePath';
import FormError from '../../../../../molecules/form/FormError';
import Form, { Data, FormErrors } from '../../../../../molecules/form/Form';
import PageContentNotFound from '../../../../../organisms/page-content-not-found/PageContentNotFound';

const ProvideTransactions = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const routePath = useRoutePath();
    const { homeseekerId } = useParams();
    const [ searchParams ] = useSearchParams();

    const authorisationId = searchParams.get('authorisationId');

    const [ , setFormData ] = useState<Data>({});
    const [ formValid, setFormValid ] = useState<boolean>(false);
    const [ formErrors, setFormErrors ] = useState<FormErrors>({});

    const [selectedIncomeVerificationMethod, setSelectedIncomeVerificationMethod] = useState<string | null>(null);

    const handleSubmit = async () => {
        if (formValid === true) {
            if (selectedIncomeVerificationMethod === 'connectBankAccount') {
                navigate(routePath('ScreeningIncomeVerificationProvideTransactionsPsd2Connection', { homeseekerId }, { authorisationId }));
            }
            if (selectedIncomeVerificationMethod === 'uploadBankStatements') {
                navigate(routePath('ScreeningIncomeVerificationProvideTransactionsBankStatements', { homeseekerId }, { authorisationId }));
            }
        }
    }

    const handleFormChange = (data, isValid, errors) => {
        setFormData(data);
        setFormValid(isValid);
        setFormErrors(errors);
    }

    const handleClick = (action: Action) => {
        if (action === 'previous') {
            navigate(routePath('ScreeningIncomeVerificationOverview', { homeseekerId }, { authorisationId }));
        }
        if (action === 'skip') {
            navigate(routePath('ScreeningAdditionalInformation', { homeseekerId }, { authorisationId }));
        }
    }

    const options = [
        {
            'label': (
                <div>
                    <Text
                        className={styles.connectBankAccount}
                        size={FontSizeEnum.Md}
                        weight={FontWeightEnum.Regular}
                    >
                        {t('screening.incomeVerification.provideTransactions.selectVerificationIncomeMethod.connectBankAccount.label')}
                        <span>&nbsp;</span>
                        <Text
                            className={styles.typeOptions}
                            color={ColorEnum.Gray400}
                            size={FontSizeEnum.Md}
                            weight={FontWeightEnum.Light}
                        >{t('screening.incomeVerification.provideTransactions.selectVerificationIncomeMethod.connectBankAccount.label.subtextOne')}</Text>
                    </Text>
                    <br/>
                    <Text
                        className={styles.typeOptions}
                        color={ColorEnum.Gray500}
                        size={FontSizeEnum.Sm}
                        weight={FontWeightEnum.Light}
                    >{t('screening.incomeVerification.provideTransactions.selectVerificationIncomeMethod.connectBankAccount.label.subtextTwo')}</Text>
                </div>
            ),
            'value': 'connectBankAccount'
        },
        {
            'label': (
                <div>
                    <Text
                        size={FontSizeEnum.Md}
                        weight={FontWeightEnum.Regular}
                    >{t('screening.incomeVerification.provideTransactions.selectVerificationIncomeMethod.uploadBankStatements.label')}</Text>
                    <br/>
                    <Text
                        color={ColorEnum.Gray500}
                        size={FontSizeEnum.Sm}
                        weight={FontWeightEnum.Light}
                    >{t('screening.incomeVerification.provideTransactions.selectVerificationIncomeMethod.uploadBankStatements.label.subtextOne')}</Text>
                </div>
            ),
            'value': 'uploadBankStatements'
        },
    ];

    if (!authorisationId) {
        return <PageContentNotFound/>;
    }

    return (
        <Form
            onSubmit={handleSubmit}
            onChange={handleFormChange}
        >
            <div className={styles.contentContainer}>
                <div className={styles.gridLg}>
                    <Text
                        color={ColorEnum.Primary700}
                        weight={FontWeightEnum.Bold}
                        size={FontSizeEnum.DisplayXs}
                    >{t(screeningSteps.ScreeningIncomeVerificationProvideTransactions.pageTitleTranslationKey)}</Text>
                    <Card elevated>
                        <Text>
                            <Trans
                                i18nKey="screening.incomeVerification.provideTransactions.supportingText"
                                components={{
                                    strong: <strong/>,
                                    tooltip:
                                        <Tooltip
                                            className={styles.tooltip}
                                            size='md'
                                            anchor={
                                                <div className={styles.tooltipAnchor}>
                                                    <QuestionLineIcon/>
                                                </div>
                                            }
                                        >{}</Tooltip>
                                }}
                            />
                        </Text>
                        <div className={styles.selectVerificationIncomeMethodTitle}>
                            <Text
                                color={ColorEnum.Gray800}
                                weight={FontWeightEnum.Semibold}
                            >{t('screening.incomeVerification.provideTransactions.selectVerificationIncomeMethod.title')}</Text>
                        </div>
                        <RadioInput
                            name='selectVerificationIncomeMethod'
                            value={selectedIncomeVerificationMethod}
                            options={options}
                            onChange={setSelectedIncomeVerificationMethod}
                            required
                        />
                        <div>
                            <FormError errors={formErrors.selectVerificationIncomeMethod} />
                        </div>
                    </Card>
                    <ScreeningNavigation
                        onClick={handleClick}
                    />
                </div>
            </div>
        </Form>
    );
}

export default ProvideTransactions;
